/**
 * Keep the scope bar's selection and the address's `?org=` in step.
 *
 * The rules, in the order they are checked:
 *
 *  1. **A change of identity** (sign-in, impersonation, a role change) leaves
 *     the address naming the previous person's selection. It is never applied:
 *     the store, which `authStore` has already reset, is written over it.
 *  2. **The address names an organization the store does not hold** (a reload,
 *     a new tab, a shared link, Back or Forward): the address wins. The page
 *     is held until the selection is applied, so nothing on it is fetched for
 *     the previous organization. One the caller cannot use falls back to their
 *     default scope with a short notice.
 *  3. **The address names none** (a sidebar link, any in-app link, a typed
 *     path): the store holds, and the address is completed to show it. That
 *     replaces the entry rather than adding one, so moving between pages
 *     carries the selection without an extra step for Back.
 *  4. **The store moved on the same page** (the bar, a drill-in): the address
 *     follows, pushed, so Back returns to the previous selection. A correction
 *     (a refused or vanished organization) replaces instead.
 *
 * Every other param is kept: a page's filters, its open sheet (`?x=`,
 * `?rate=`), its grouping. Only `org` is written.
 *
 * Company-level roles have no bar, so nothing is written for them and an
 * `?org=` that reaches them is removed.
 */

import { useCallback, useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import { useScopeDataSnapshot } from "@/hooks/scopeData";
import { useScopeKey } from "@/hooks/useScopeKey";
import { Role } from "@/lib/constants/roles";
import {
  DEFAULT_TOKEN,
  ORG_PARAM,
  canonicalOrg,
  fallBackToDefault,
  noticeScopeFallback,
  parseOrgParam,
  resolveOrgParam,
  takeScopeCorrection,
  tokenOf,
  withOrgParam,
  type FallbackReason,
  type OrgReach,
} from "@/lib/scope/orgParam";
import { useAuthStore } from "@/store/authStore";
import { useContextStore, type ViewContext } from "@/store/contextStore";

type Plan =
  /** Address and store agree. */
  | { kind: "idle" }
  /** The store is ahead: write its selection into the address. */
  | { kind: "write"; replace: boolean }
  /** The address is ahead: select what it names. */
  | { kind: "apply"; selection: ViewContext; token: string; respell: boolean }
  /** The address names something the caller cannot use. */
  | { kind: "refuse"; reason: FallbackReason }
  /** The address is ahead and the org lists needed to read it are in flight. */
  | { kind: "wait" }
  /** A caller with no bar: drop the param, explaining if it named another organization. */
  | { kind: "strip"; notice: FallbackReason | null };

/** What the last settled render agreed on. */
interface Settled {
  /** The history entry it was on. */
  key: string;
  /** The selection the address and the store both held. */
  token: string;
  /** Who the session was. */
  identity: string;
}

/** The roles the scope bar is shown to. */
function hasBar(role: string | undefined): boolean {
  return (
    role === Role.PLATFORM_ADMIN ||
    role === Role.PLATFORM_MANAGER ||
    role === Role.DISTRIBUTOR_ADMIN ||
    role === Role.MSP_ADMIN
  );
}

function isPlatformStaff(role: string | undefined): boolean {
  return role === Role.PLATFORM_ADMIN || role === Role.PLATFORM_MANAGER;
}

/** Why a value was dropped for a caller with no bar, or `null` if it was theirs anyway. */
function stripReason(
  raw: string | null,
  ownCompany: string | undefined,
): FallbackReason | null {
  const param = parseOrgParam(raw);
  if (param.kind === "absent" || param.kind === "default") return null;
  if (param.kind === "malformed") return "malformed";
  if (param.kind === "all") return "role";
  // Their own company is the view they already have.
  return ownCompany && param.id === ownCompany.toLowerCase() ? null : "unknown";
}

/**
 * Sync the address and the scope bar. Mounted once, by `ScopedOutlet`.
 *
 * Returns `holding` while the address names an organization the store has not
 * taken yet: the page must not render until it has, or it would fetch for the
 * previous one (and a page whose reads do not follow the bar would keep those
 * rows).
 */
export function useOrgParamSync(): { holding: boolean } {
  const location = useLocation();
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const impersonating = useAuthStore((s) => s.impersonation.isImpersonating);
  const scopeKey = useScopeKey();
  const lists = useScopeDataSnapshot();

  const role = user?.role;
  const bar = hasBar(role);
  const identity = `${user?.id ?? ""}|${role ?? ""}|${impersonating ? "as" : ""}`;
  const storeToken = tokenOf({
    scope: scopeKey.scope as ViewContext["scope"],
    distributorId: scopeKey.distributor,
    mspId: scopeKey.msp,
    companyId: scopeKey.company,
  });
  const raw = new URLSearchParams(location.search).get(ORG_PARAM);

  const settled = useRef<Settled | null>(null);
  // The plan last carried out, so a re-run of the same effect (React's
  // development double-invoke, or a render that changed nothing) does not push
  // a second history entry.
  const carriedOut = useRef<string | null>(null);

  const plan = ((): Plan => {
    if (!bar) {
      if (raw === null) return { kind: "idle" };
      return { kind: "strip", notice: stripReason(raw, user?.companyId) };
    }
    const done = settled.current;
    // Rule 1. The address is the previous identity's; the store is not.
    if (done && done.identity !== identity) return { kind: "write", replace: true };

    const navigated = !done || done.key !== location.key;
    // Rule 4.
    if (!navigated) {
      return storeToken === done.token
        ? { kind: "idle" }
        : { kind: "write", replace: false };
    }

    const param = parseOrgParam(raw);
    // Rule 3.
    if (param.kind === "absent") return { kind: "write", replace: true };

    const urlToken = canonicalOrg(param);
    if (urlToken === storeToken) {
      // Agreed, but spelled differently (an upper-case id): tidy the address.
      return raw === urlToken ? { kind: "idle" } : { kind: "write", replace: true };
    }
    // A pick and a navigation in one handler: the new entry carried the old
    // selection along from the page it was built on, and the store has
    // already moved past it. The store is the newer word.
    if (done && storeToken !== done.token && urlToken === done.token) {
      return { kind: "write", replace: true };
    }

    // Rule 2.
    const reach: OrgReach = {
      isPlatformStaff: isPlatformStaff(role),
      allowedDistributorIds: user?.allowedDistributorIds ?? [],
      allowedMspIds: user?.allowedMspIds ?? [],
      distributors: lists.distributors,
      msps: lists.msps,
      companies: lists.companies,
      loading: lists.isLoading,
      incomplete: lists.incomplete,
    };
    const resolved = resolveOrgParam(param, reach);
    if (resolved.kind === "pending") return { kind: "wait" };
    if (resolved.kind === "refused") return { kind: "refuse", reason: resolved.reason };
    return {
      kind: "apply",
      selection: resolved.selection,
      token: urlToken as string,
      respell: raw !== urlToken,
    };
  })();

  const signature = `${location.key}|${identity}|${storeToken}|${raw ?? ""}|${plan.kind}`;

  useEffect(() => {
    if (carriedOut.current === signature) return;
    carriedOut.current = signature;

    /** Write `token` into the address, keeping every other param. */
    const write = (token: string | null, replace: boolean) => {
      const params = new URLSearchParams(location.search);
      if (token === null) params.delete(ORG_PARAM);
      else params.set(ORG_PARAM, token);
      const search = params.toString();
      navigate(
        {
          pathname: location.pathname,
          search: search ? `?${search}` : "",
          hash: location.hash,
        },
        { replace, state: location.state },
      );
    };
    const settle = (token: string) => {
      settled.current = { key: location.key, token, identity };
    };

    switch (plan.kind) {
      case "idle":
        settle(storeToken);
        return;
      case "wait":
        return;
      case "strip":
        write(null, true);
        if (plan.notice) noticeScopeFallback(plan.notice);
        settle(DEFAULT_TOKEN);
        return;
      case "write": {
        // The store as it is now, not as it was at render: a page's own effect
        // in this same commit may have moved it (a link param it turns into a
        // pick), and writing the older value would then read back as the
        // address being ahead and undo the page's pick.
        const live = tokenOf(useContextStore.getState());
        const correction = takeScopeCorrection(live);
        // Already what the address says (the store moved back before this
        // ran): a push of the same address would only add an entry to Back.
        if (raw === live) {
          settle(live);
          return;
        }
        write(live, plan.replace || correction !== null);
        if (correction?.notice) noticeScopeFallback(correction.notice);
        settle(live);
        return;
      }
      case "apply": {
        // A correction waiting for the address is superseded: the address
        // named an organization, and that is what is shown.
        takeScopeCorrection("");
        if (tokenOf(useContextStore.getState()) !== plan.token) {
          useContextStore.getState().setContext(plan.selection);
        }
        if (plan.respell) write(plan.token, true);
        settle(plan.token);
        return;
      }
      case "refuse":
        fallBackToDefault(plan.reason);
        takeScopeCorrection(DEFAULT_TOKEN);
        write(DEFAULT_TOKEN, true);
        settle(DEFAULT_TOKEN);
        return;
    }
    // `signature` names everything the plan was built from; the plan object
    // itself is rebuilt on every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [signature]);

  return {
    holding: plan.kind === "apply" || plan.kind === "refuse" || plan.kind === "wait",
  };
}

/**
 * `href` carrying the current selection as `?org=`, for links the shell
 * builds.
 *
 * A link that leaves the param off still lands right (the address is
 * completed on arrival), but only in this tab. Carried on the link itself,
 * opening it in a new tab or copying it reproduces the same narrowed list.
 * Callers with no bar get `href` unchanged.
 */
export function useOrgHref(): (href: string) => string {
  const role = useAuthStore((s) => s.user?.role);
  const scopeKey = useScopeKey();
  const bar = hasBar(role);
  const token = tokenOf({
    scope: scopeKey.scope as ViewContext["scope"],
    distributorId: scopeKey.distributor,
    mspId: scopeKey.msp,
    companyId: scopeKey.company,
  });
  return useCallback(
    (href: string) => (bar ? withOrgParam(href, token) : href),
    [bar, token],
  );
}
